import { Component, OnDestroy } from '@angular/core';
import { BsModalRef, BsModalService, ModalModule } from 'ngx-bootstrap/modal';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { AttachmentsModalComponent } from '../attachments-modal/attachments-modal.component';
import { ReplyTemplateManagerService } from 'src/sb-tools/typescript/services/reply-template-manager.service';
import { HelperService } from 'src/sb-tools/typescript/services/helper.service';
import { AttachmentType } from 'src/sb-tools/typescript/enums/attachment-type.enum';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { QuillHelperService } from 'src/sb-tools/typescript/services/quill/quill-helper.service';
import { SmilesService } from 'src/sb-tools/typescript/services/smiles.service';
import { ReplyTemplatesModalComponent } from 'src/sb-modules/Frontend-Reply-Templates-Module/reply-templates-modal.component';
import { ATTACHMENTS_MODAL_ID, REPLY_TEMPLATE_MODAL_ID } from 'src/sb-tools/typescript/constants/nested-modal-ids';
import { ReplyUpdateModel } from 'src/sb-tools/typescript/models/reply-update-model.model';
import { MessageDTO } from '@sb-tools/typescript/models/dto/message.dto';
import { ReplyMentionsToCodePipe } from '../../pipes/reply-mentions-to-code.pipe';
import { NotificationsService } from 'angular2-notifications';
import Quill from 'quill';
import { Range } from 'quill/core/selection';
import { AttachmentBase } from '@sb-tools/typescript/models/attachment-base.model';
import { ReplyTemplateService } from '../../../../sb-modules/Frontend-Reply-Templates-Module/core/reply-template.service';
import { RightPanelType } from '../../../core/enums/right-panel-type.enum';
import { ReplyDataService } from '../../../core/services/ticket/reply-data.service';
import { ReplyStoreService } from '../../../core/services/ticket/reply-store.service';
import { RightPanelService } from '../../../core/services/right-panel.service';
import { InlineSVGComponent } from '../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { UserAvatarComponent } from '../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { HighlightReplyMentionComponent } from '../../components/highlight-reply-mention/highlight-reply-mention.component';
import { FileViewComponent } from '../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-view/file-view.component';
import { DatePipe, NgClass } from '@angular/common';
import { FilePreviewComponent } from '../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { QuillReplyTemplatesComponent } from '../../../../sb-modules/Frontend-Reply-Templates-Module/quill-reply-templates/quill-reply-templates.component';
import { QuillEditorComponent } from 'ngx-quill';
import { FormsModule } from '@angular/forms';
import { QuillEmojiPickerComponent } from '../../../../sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { GiphyPickerComponent } from '../../../../sb-modules/Frontend-GIF-Module/giphy-picker/giphy-picker.component';
import { EncodeHtmlSymbolsPipe } from '../../../../sb-tools/typescript/pipes/encode-html-symbols.pipe';
import { IsInvalidMessagePipe } from '../../../../sb-tools/typescript/pipes/ticket/is-invalid-message.pipe';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';
import { FilterContentAttachmentsPipe } from '../../pipes/filter-content-attachments.pipe';

@Component({
    selector: 'app-add-private-reply-modal',
    templateUrl: './add-private-reply-modal.component.html',
    styleUrls: ['./add-private-reply-modal.component.scss'],
    imports: [
        TranslateModule,
        InlineSVGComponent,
        UserAvatarComponent,
        HighlightReplyMentionComponent,
        FileViewComponent,
        NgClass,
        FilePreviewComponent,
        TooltipModule,
        QuillReplyTemplatesComponent,
        QuillEditorComponent,
        FormsModule,
        QuillEmojiPickerComponent,
        GiphyPickerComponent,
        ModalModule,
        DatePipe,
        EncodeHtmlSymbolsPipe,
        IsInvalidMessagePipe,
        TimeAgoPipe,
        FilterContentAttachmentsPipe,
    ],
})
export class AddPrivateReplyModalComponent implements OnDestroy {
    socialMediaSourceId: number;

    ticket;
    reply?;
    newReply? = {
        text: '',
        html: '',
        attachments: [],
    };
    isShowGiphyPicker = true;
    isAdding = false;
    isNeedSecondBackdrop = false;

    rightPanelTypeEnum = RightPanelType;
    attachmentTypeEnum = AttachmentType;
    ticketSourceEnum = TicketSource;

    quillEditor: Quill;
    quillRange: Range;
    quillConfig = {
        toolbar: false,
        imageDrop: {
            saveAsAttachment: (file) => {
                this.quillHelperService.saveAsAttachment(this.newReply.attachments, file);
            },
            isAddingAsAttachment: true,
        },
    };

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        public replyTemplateManagerService: ReplyTemplateManagerService,
        public rightPanelService: RightPanelService,
        public replyTemplateService: ReplyTemplateService,
        private bsModalService: BsModalService,
        private modalRef: BsModalRef,
        private smilesService: SmilesService,
        private translateService: TranslateService,
        private replyStoreService: ReplyStoreService,
        private quillHelperService: QuillHelperService,
        private replyMentionsToCodePipe: ReplyMentionsToCodePipe,
        private replyDataService: ReplyDataService,
        private notificationsService: NotificationsService,
    ) {}

    addGifAttachment(gif: AttachmentBase) {
        this.newReply.attachments.push(gif);
        this.isShowGiphyPicker = false;
    }

    addReply() {
        if (this.isAdding) {
            return;
        }

        this.isAdding = true;
        this.addPrivateReply(this.newReply, this.reply);
    }

    deleteGifAttachment(attachment: AttachmentBase) {
        const index = this.newReply.attachments.indexOf(attachment);
        this.newReply.attachments.splice(index, 1);
        this.isShowGiphyPicker = true;
    }

    onEditorCreated(quill) {
        this.quillEditor = quill;
        quill.focus();
    }

    onReplyChanged(data, reply) {
        reply.text = HelperService.strip(this.smilesService.replaceImagesToCodes(reply.html), false);
    }

    openAttachmentsModal() {
        this.isNeedSecondBackdrop = true;

        const initialState = {
            modalId: ATTACHMENTS_MODAL_ID,
            newAttachments: this.newReply.attachments,
            existedAttachmentsLength: this.newReply.attachments.length,
        };

        this.bsModalService.show(AttachmentsModalComponent, {
            initialState,
            id: ATTACHMENTS_MODAL_ID,
            ignoreBackdropClick: true,
        });
        this.bsModalService.onHide.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.isNeedSecondBackdrop = false;
        });
    }

    openReplyTemplatesModal($event, quillEditor) {
        this.isNeedSecondBackdrop = true;
        this.updateQuillSelection(quillEditor);

        const initialState = {
            modalId: REPLY_TEMPLATE_MODAL_ID,
            isTextContent: true,
            confirmCallback: (templateText, attachments) => {
                return this.replyTemplateManagerService.chooseReplyTemplate({
                    $event,
                    templateText,
                    quillEditor,
                    quillRange: this.quillRange,
                    reply: this.newReply,
                    attachments,
                });
            },
        };

        this.bsModalService.show(ReplyTemplatesModalComponent, {
            initialState,
            id: REPLY_TEMPLATE_MODAL_ID,
        });

        this.bsModalService.onHide.pipe(takeUntil(this.unsubscriber)).subscribe(() => {
            this.isNeedSecondBackdrop = false;
        });
    }

    updateQuillSelection(quill) {
        this.quillRange = this.replyTemplateManagerService.updateRange(quill);
    }

    private addPrivateReply(newReply, reply) {
        const isFormEmailApi = [TicketSource.form, TicketSource.email].includes(this.ticket.source);
        const newReplyModel: ReplyUpdateModel = {
            attachments: newReply.attachments,
            isDraft: false,
            isReplySentAsLink: false,
            usersToMention: [],
            message: new MessageDTO(
                isFormEmailApi ? newReply.html : null,
                // todo: rewrite this conditions
                /* eslint-disable */
                isFormEmailApi
                    ? null
                    : this.ticket.constraints.isUserMentioningAllowed
                      ? HelperService.strip(
                            this.smilesService.replaceImagesToCodes(
                                this.replyMentionsToCodePipe.transform(newReply.html),
                            ),
                            false,
                        )
                      : newReply.text,
                /* eslint-enable */
            ),
            isCompleted: false,
            isRecipientChangeConfirmed: newReply.isRecipientChangeConfirmed,
            parentId: reply && reply.id,
            senderEmail: this.commonDataService.user.email,
            senderName: this.commonDataService.user.name,
            toEmails: this.ticket.toEmails,
            ccEmails: [],
            bccEmails: [],
            receiverEmail: null,
            socialMediaSourceId: this.socialMediaSourceId,
            ticketId: this.ticket.id,
        };

        if (reply) {
            this.replyDataService
                .addPrivateReplyForReply(this.ticket.id, reply.id, newReplyModel)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((data) => {
                    reply.privateReplyTicketId = data;
                    this.notificationsService.success(
                        this.translateService.instant('Success!'),
                        this.translateService.instant('Your reply was successfully sent.'),
                    );
                    this.modalRef.hide();
                });
        } else {
            this.replyDataService
                .addPrivateReplyForTicket(this.ticket.id, newReplyModel)
                .pipe(takeUntil(this.unsubscriber))
                .subscribe((data) => {
                    this.ticket.privateReplyTicketId = data;
                    this.notificationsService.success(
                        this.translateService.instant('Success!'),
                        this.translateService.instant('Your reply was successfully sent.'),
                    );
                    this.modalRef.hide();
                });
        }
    }

    decline(): void {
        this.modalRef.hide();
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
